import * as React from 'react'

import { Box, Button, RadioButton, Text } from '@factorialco/deprecated-design-system'
import { isEmpty, omitBy } from 'lodash'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import useCreateTrialAction from 'modules/onboarding/hooks/trialActions/useCreateTrialAction'
import useNewContactRequest from 'modules/onboarding/hooks/useNewContactRequest'

interface Props {
  onClose: () => void
}

const CloseReasonModal = ({ onClose }: Props) => {
  const { create: createTrialAction } = useCreateTrialAction()
  const { action, eventEmitter } = useNewContactRequest({
    flashError: true,
    flashSuccess: true,
  })

  const reasons = [
    {
      id: 'need_more_time',
      label: i18n.t('pricing_plans.billing_information.close_reason_form.options.need_more_time'),
    },
    {
      id: 'no_decision_maker',
      label: i18n.t(
        'pricing_plans.billing_information.close_reason_form.options.no_decision_maker'
      ),
    },
    {
      id: 'talk_with_agent',
      label: i18n.t('pricing_plans.billing_information.close_reason_form.options.talk_with_agent'),
    },
    {
      id: 'too_expensive',
      label: i18n.t('pricing_plans.billing_information.close_reason_form.options.too_expensive'),
    },
    {
      id: 'lack_of_payment_methods',
      label: i18n.t(
        'pricing_plans.billing_information.close_reason_form.options.lack_of_payment_method'
      ),
      extended_field_label: i18n.t(
        'pricing_plans.billing_information.close_reason_form.options.labels.lack_of_payment_method'
      ),
    },
    {
      id: 'payment_method_problems',
      label: i18n.t(
        'pricing_plans.billing_information.close_reason_form.options.payment_method_problems'
      ),
      extended_field_label: i18n.t(
        'pricing_plans.billing_information.close_reason_form.options.labels.payment_method_problems'
      ),
    },
    {
      id: 'other',
      label: i18n.t('pricing_plans.billing_information.close_reason_form.options.other'),
      extended_field_label: i18n.t(
        'pricing_plans.billing_information.close_reason_form.options.labels.other'
      ),
    },
  ]

  const [closing, setClosing] = React.useState(false)
  const [closeReason, setCloseReason] = React.useState('')
  const [extendedAnswer, setExtendedAnswer] = React.useState('')

  const closeUpgradePlanModal = async (sendFeedback: boolean) => {
    setClosing(true)
    if (closeReason === 'talk_with_agent') {
      const { success } = await action()

      if (success) {
        eventEmitter('Dropped billing step')
      }
    }

    const amplitudeProps = sendFeedback
      ? { feedback_answer: closeReason, extended_answer: extendedAnswer }
      : {}

    track(trackingEvents.billing.upgradePlanCancel, { ...omitBy(amplitudeProps, isEmpty) })

    await createTrialAction({
      droppedBillingStep: true,
      reason: closeReason,
      reasonOther: extendedAnswer,
    })

    onClose()
  }

  return (
    <Modal
      locked
      illustration="document-edit"
      title={i18n.t('pricing_plans.billing_information.close_reason_form.title')}
      description={i18n.t('pricing_plans.billing_information.close_reason_form.description')}
      footer={
        <Button
          disabled={closing || closeReason === ''}
          onClick={() => closeUpgradePlanModal(true)}
        >
          {i18n.t('pricing_plans.billing_information.close_reason_form.submit')}
        </Button>
      }
    >
      <Box gap="s12">
        {reasons.map(({ id, label, extended_field_label }, index) => {
          return (
            <Box key={index} gap="s12">
              <Box
                component="label"
                flexDirection="row"
                alignItems="center"
                gap="s8"
                onClick={() => setCloseReason(id)}
              >
                <RadioButton
                  value={id}
                  checked={closeReason === id}
                  onChange={() => setCloseReason(id)}
                />
                <Text.Normal>{label}</Text.Normal>
              </Box>
              {extended_field_label && closeReason === id && (
                <TextArea
                  label={extended_field_label}
                  value={extendedAnswer}
                  onChange={(value: string) => setExtendedAnswer(value)}
                />
              )}
            </Box>
          )
        })}
      </Box>
    </Modal>
  )
}

export default CloseReasonModal
